import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Lock, Vault, Eye, Wrench, CheckCircle2, Building2, Landmark, Hotel } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

export default function Home() {
  const solutions = [
    {
      icon: Vault,
      title: 'Vault Systems',
      description: 'UL-rated vault doors up to TL-30x6 with time-delay mechanisms, advanced relocking systems, and custom configurations. Composite steel and concrete construction with up to 4-hour fire rating.',
      image: 'https://images.unsplash.com/photo-1582139329536-e7284fece509?w=800&q=85',
    },
    {
      icon: Lock,
      title: 'Biometric Access',
      description: 'Multi-factor authentication with fingerprint, facial, and iris recognition. <1 second recognition speed, 99.9%+ accuracy, supports up to 10,000 users. Anti-spoofing technology included.',
      image: 'https://images.unsplash.com/photo-1668609537021-f377356358f9?w=800&q=85',
    },
    {
      icon: Shield,
      title: 'Armored Doors',
      description: 'Ballistic and blast-resistant steel doors rated up to NIJ Level IV. Forced-entry rated (RC3-RC6), weatherproof, corrosion-resistant with thickness from 50mm to 120mm. CE, UL, ISO certified.',
      image: 'https://images.unsplash.com/photo-1705776919715-fa052f797923?w=800&q=85',
    },
    {
      icon: Eye,
      title: 'Access Control',
      description: 'Centralized management for unlimited doors. Card, mobile, and PIN-based access with real-time monitoring, CCTV integration, cloud or on-premise options. Mobile apps for iOS & Android.',
      image: 'https://images.unsplash.com/photo-1697382608786-bcf4c113b86e?w=800&q=85',
    },
    {
      icon: Shield,
      title: 'Fire-Resistant',
      description: 'Fire ratings from FD30 to FD240 (up to 240 minutes). Smoke and temperature seals, panic hardware compatible. Meets BS, EN, NFPA standards with security + fire code compliance.',
      image: 'https://images.pexels.com/photos/965878/pexels-photo-965878.jpeg?w=800&q=85',
    },
    {
      icon: Wrench,
      title: 'Maintenance',
      description: '<4 hours emergency response time across UK & USA. Scheduled inspections, emergency repairs, parts replacement, and compliance documentation. Manufacturer-certified technicians.',
      image: 'https://images.pexels.com/photos/64609/pexels-photo-64609.jpeg?w=800&q=85',
    },
  ];

  const clients = [
    { icon: Landmark, name: 'Banking Sector' },
    { icon: Building2, name: 'Government' },
    { icon: Hotel, name: 'Hospitality' },
  ];

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative overflow-hidden scan-beam bg-background">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            {/* Content */}
            <motion.div
              className="lg:col-span-6 space-y-6"
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              transition={{ duration: 0.6 }}
            >
              <Badge variant="outline" className="text-accent border-accent">
                Trusted Since 2020
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-foreground tracking-tight leading-tight">
                Uncompromised Security.<br />Engineered for the Future.
              </h1>
              <p className="text-base md:text-lg text-muted-foreground max-w-prose leading-relaxed">
                High-level security door systems trusted by banks, governments, and institutions worldwide. 
                Installation, supply, and maintenance across London and New York.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button
                    data-testid="home-hero-primary-cta"
                    size="lg"
                    className="bg-primary hover:bg-primary/90 h-12 px-8"
                  >
                    Request a Quote
                  </Button>
                </Link>
                <Link to="/projects">
                  <Button
                    data-testid="home-hero-secondary-cta"
                    variant="ghost"
                    size="lg"
                    className="h-12 px-8 border border-border hover:bg-foreground/5"
                  >
                    View Case Studies
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Visual */}
            <motion.div
              className="lg:col-span-6 relative h-[320px] sm:h-[420px] lg:h-[520px] metallic-surface rounded-xl overflow-hidden"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              aria-hidden="true"
            >
              <img
                src="https://images.unsplash.com/photo-1582139329536-e7284fece509?w=1200&q=85"
                alt="Industrial vault detail"
                className="absolute inset-0 w-full h-full object-cover opacity-70 mix-blend-luminosity"
              />
              <div className="noise-overlay rounded-xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trusted Clients */}
      <section className="py-12 bg-card border-y border-border">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-sm text-muted-foreground font-medium">TRUSTED BY</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 items-center justify-items-center">
            {clients.map((client, index) => (
              <motion.div
                key={index}
                className="flex items-center space-x-3 text-muted-foreground"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <client.icon className="h-8 w-8 text-secondary" />
                <span className="text-base font-medium">{client.name}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Bento */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-semibold mb-4">Comprehensive Security Solutions</h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              From reinforced entry systems to biometric access control, we deliver end-to-end security solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="group bg-card border-border/60 hover:border-accent/40 transition-all duration-300 overflow-hidden h-full">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={solution.image}
                      alt={solution.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent opacity-60" />
                  </div>
                  <CardHeader>
                    <div className="flex items-center space-x-3 mb-2">
                      <solution.icon className="h-6 w-6 text-accent" />
                      <CardTitle className="text-lg">{solution.title}</CardTitle>
                    </div>
                    <CardDescription className="text-sm">{solution.description}</CardDescription>
                  </CardHeader>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/solutions">
              <Button variant="outline" size="lg" className="border-border hover:bg-foreground/5">
                Explore All Solutions
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-16 bg-background border-y border-border">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="text-4xl font-bold text-primary font-mono tabular-nums mb-2">500+</div>
              <div className="text-sm text-muted-foreground">Installations Completed</div>
            </motion.div>
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="text-4xl font-bold text-primary font-mono tabular-nums mb-2">24/7</div>
              <div className="text-sm text-muted-foreground">Emergency Support</div>
            </motion.div>
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="text-4xl font-bold text-primary font-mono tabular-nums mb-2">5+</div>
              <div className="text-sm text-muted-foreground">Years in Operation</div>
            </motion.div>
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <div className="text-4xl font-bold text-primary font-mono tabular-nums mb-2">100%</div>
              <div className="text-sm text-muted-foreground">Client Satisfaction</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Certifications & Global Footprint */}
      <section className="py-16 bg-card border-t border-border">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Certifications */}
            <div>
              <h3 className="text-2xl font-semibold mb-6">Certified Excellence</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">ISO 9001:2015 Quality Management</p>
                    <p className="text-sm text-muted-foreground">Internationally recognized quality standards</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">UL 752 Ballistic Resistance</p>
                    <p className="text-sm text-muted-foreground">Certified bulletproof protection levels</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">CE Marking Compliance</p>
                    <p className="text-sm text-muted-foreground">European safety and security standards</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Global Presence */}
            <div>
              <h3 className="text-2xl font-semibold mb-6">Global Operations</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-lg">London Headquarters</p>
                    <p className="text-sm text-muted-foreground">14 Canary Wharf, London, UK</p>
                    <p className="text-sm text-muted-foreground mt-1">Full logistics & installation center</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-lg">New York Operations</p>
                    <p className="text-sm text-muted-foreground">280 Hudson Street, NY, USA</p>
                    <p className="text-sm text-muted-foreground mt-1">Warehouse & technical support</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
